import type { ResourceOccupancyEntry } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import type { EntryState } from './resourceCalendarModel';
import type { GridBlock } from './resourceGridModel';

const TONE: Record<EntryState, string> = {
  reserved: 'bg-forest text-white',
  held: 'bg-amber-tint text-amber-ink',
  blocked:
    'bg-[repeating-linear-gradient(135deg,var(--color-sand)_0_6px,var(--color-paper)_6px_12px)] text-ink-soft',
  clarification: 'bg-sealwax-tint text-sealwax',
  cancelled: 'bg-card-alt text-ink-soft line-through opacity-70',
};

/**
 * Ein Block im Tagesraster. Wer verwalten darf, klickt ihn zum Aufheben an; Aufgehobenes ist nur
 * noch Spur und lässt den Zug darunter durch. Ist der Block schmaler als ein Wort, trägt er keinen
 * Text – ein abgeschnittenes „R…“ ist keine Beschriftung; Titel und Vorleser nennen ihn ganz.
 */
export function ResourceGridBlock({
  block,
  canManage,
  onCancel,
}: {
  block: GridBlock;
  canManage: boolean;
  onCancel: (entry: ResourceOccupancyEntry) => void;
}) {
  const style = { left: `calc(${block.left}% + 2px)`, width: `calc(${block.width}% - 4px)` };
  const className = cn(
    '@container absolute top-[10px] flex h-[38px] items-center overflow-hidden whitespace-nowrap rounded-[10px] px-2.5 text-[11px] font-bold',
    TONE[block.state],
  );
  const text = <span className="@max-[5.5rem]:hidden truncate">{block.label}</span>;
  const name = `${block.label}, ${block.time}`;
  if (block.state === 'cancelled') {
    return (
      <span title={name} className={cn(className, 'pointer-events-none')} style={style}>
        {text}
      </span>
    );
  }
  return canManage ? (
    <button
      type="button"
      title={name}
      aria-label={`${name} – aufheben`}
      onClick={() => onCancel(block.entry)}
      className={className}
      style={style}
    >
      {text}
    </button>
  ) : (
    <span title={name} role="img" aria-label={name} className={className} style={style}>
      {text}
    </span>
  );
}
